import { getMobileOperatingSystem } from 'helpers/getMobileOperatingSystem'
import { IEvent, IEventLogger, TQuestionPageParams } from 'models/events.model'
import { EventLoggerInstanceName, ScreenName } from 'root-constants'
import { PaymentMethod, PurchasePageType } from 'modules/purchase/constants'
import { getCurrentOnboardingVariant } from 'helpers/getCurrentOnboardingVariant'
import { TProductId } from 'models/subscriptions.model'
import { getAbSegmentName } from 'helpers/getAbSegmentName'

export const enum Events {
  SESSION_STARTED = 'session_start',
  AB_SEGMENT = 'ab_segment',
  QUESTION_COMPLETED = 'question_page_completed',
  EMAIL_PAGE_SHOW = 'email_page_show',
  EMAIL_PAGE_COMPLETED = 'email_page_completed',
  RESULT_PAGE_SHOW = 'results_page_show',
  RESULT_PAGE_COMPLETED = 'results_page_completed',
  SALE_SCREEN_SHOW = 'plans_page_show',
  TERMS_OF_USE = 'terms_of_use_tap',
  PRIVACY_POLICY = 'privacy_policy_tap',
  NEED_HELP = 'need_help_tap',
  PURCHASE_SHOW = 'subs_purchase_show',
  PAYMENT_METHOD_SELECTED = 'payment_method_selected',
  PURCHASE_STARTED = 'subs_purchase_started',
  PURCHASE_COMPLETED = 'subs_purchase_completed',
  PURCHASE_FAILED = 'subs_purchase_failed',
  PURCHASE_SCREEN_CLOSED = 'subs_purchase_screen_close',
  FINISH_ACCOUNT_SCREEN_VIEW = 'finish_account_screen_view',
  DOWNLOAD_BTN_PRESSED = 'download_button_press',
  PAYPAL_PAYMENT_POPUP_CLOSE = 'paypal_payment_popup_close',
}

class EventLoggerService {
  private loggers?: Map<EventLoggerInstanceName, IEventLogger>
  private eventsQueue: IEvent[] = []

  init(...loggers: IEventLogger[]): void {
    const entriesArr = loggers.map(
      (logger) =>
        [logger.name, logger] as [EventLoggerInstanceName, IEventLogger],
    )
    this.loggers = new Map(entriesArr)
    this.notifyInitFinished()
  }

  logSessionStarted(): void {
    const event = Events.SESSION_STARTED
    const eventProperty = {
      device_type: getMobileOperatingSystem(),
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logAbSegmentName({
    optimizeVariantId,
    optimizeExperimentId,
    optimizeMap,
    abSegmentName,
  }: {
    optimizeVariantId: string
    optimizeExperimentId: string
    optimizeMap: Record<string, Record<string, string>> | null
    abSegmentName: string
  }): void {
    const event = Events.AB_SEGMENT
    const eventProperty = {
      ab_variant: getCurrentOnboardingVariant(optimizeVariantId),
      optimize_experiment_id: optimizeExperimentId,
      ab_segment_name:
        abSegmentName ||
        getAbSegmentName({
          optimizeMap,
          optimizeExperimentId,
          optimizeVariantId,
        }),
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logQuestion({ question, answers }: TQuestionPageParams): void {
    const event = Events.QUESTION_COMPLETED
    const eventProperty = {
      question,
      answer: Array.isArray(answers) ? answers.join(',') : answers,
    }

    this.logEventOrPushToQueue(event, eventProperty)
  }

  logPayPalPaymentPopupClose(): void {
    const event = Events.PAYPAL_PAYMENT_POPUP_CLOSE
    this.logEventOrPushToQueue(event)
  }

  // EmailWrapper Page Events
  logEmailPageShown(): void {
    const event = Events.EMAIL_PAGE_SHOW
    this.logEventOrPushToQueue(event)
  }

  logEmailPageCompleted(eventProperty: { email: string }): void {
    const event = Events.EMAIL_PAGE_COMPLETED
    this.logEventOrPushToQueue(event, eventProperty)
  }

  // Sale Page Events
  logSalePageShown({
    productIds,
    screenName = ScreenName.ONBOARDING,
  }: {
    productIds: string[]
    screenName?: ScreenName
  }): void {
    const event = Events.SALE_SCREEN_SHOW
    const eventProperty = {
      product_id: productIds.join(','),
      screen_name: screenName,
    }

    this.logEventOrPushToQueue(event, eventProperty)
  }

  logTermsOfUseClicked(): void {
    const event = Events.TERMS_OF_USE
    this.logEventOrPushToQueue(event)
  }

  logPrivacyPolicyClicked(): void {
    const event = Events.PRIVACY_POLICY
    this.logEventOrPushToQueue(event)
  }

  logNeedHelpClicked(): void {
    const event = Events.NEED_HELP
    this.logEventOrPushToQueue(event)
  }

  // Getting App Page Events
  logGettingAppShown(): void {
    const event = Events.FINISH_ACCOUNT_SCREEN_VIEW
    this.logEventOrPushToQueue(event)
  }

  logDownloadClicked(cb: () => void): void {
    const event = Events.DOWNLOAD_BTN_PRESSED
    this.logEventOrPushToQueue(event, {}, cb)
  }

  // Purchase events
  logResultPageShown(pageType: PurchasePageType): void {
    const event = Events.RESULT_PAGE_SHOW
    const eventProperty = {
      page_type: pageType,
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logResultPageCompleted(): void {
    const event = Events.RESULT_PAGE_COMPLETED
    this.logEventOrPushToQueue(event)
  }

  logPurchaseShown({
    productId,
    screenName = ScreenName.ONBOARDING,
  }: {
    productId: TProductId
    screenName?: ScreenName
  }): void {
    const event = Events.PURCHASE_SHOW
    const eventProperty = {
      product_id: productId,
      screen_name: screenName,
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logPaymentMethodSelected(paymentMethod: PaymentMethod): void {
    const event = Events.PAYMENT_METHOD_SELECTED
    const eventProperty = {
      payment_method: paymentMethod,
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logPurchaseStarted({
    productId,
    priceDetails: { price, trial = false, currency },
    paymentMethod,
    screenName,
  }: {
    productId: TProductId
    priceDetails: { price: number; trial?: boolean; currency: string }
    paymentMethod: PaymentMethod
    screenName: ScreenName
  }): void {
    const event = Events.PURCHASE_STARTED
    const eventProperty = {
      trial,
      price,
      currency,
      product_id: productId,
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
      screen_name: screenName,
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logPurchaseCompleted({
    productId,
    priceDetails: { price, trial = false, currency },
    paymentMethod,
    discountApplied,
    screenName,
  }: {
    productId: TProductId
    priceDetails: { price: number; trial?: boolean; currency: string }
    paymentMethod?: PaymentMethod
    discountApplied?: string
    screenName: ScreenName
  }): void {
    const event = Events.PURCHASE_COMPLETED
    const eventProperty = {
      trial,
      price,
      currency,
      product_id: productId,
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
      screen_name: screenName,
      ...(discountApplied && { discount_applied: discountApplied }),
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logPurchaseFailed({
    productId,
    priceDetails: { price, trial = false, currency },
    error: { description, type, code },
    paymentMethod,
    screenName,
  }: {
    productId: TProductId
    priceDetails: { price: number; trial?: boolean; currency: string }
    error: { type: string; description?: string; code?: string }
    paymentMethod?: PaymentMethod
    screenName: ScreenName
  }): void {
    const event = Events.PURCHASE_FAILED
    const eventProperty = {
      trial,
      price,
      currency,
      error_type: type,
      ...(description && { error_description: description }),
      ...(code && { error_code: code }),
      product_id: productId,
      screen_name: screenName,
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  logPurchaseScreenClosed({
    productId,
    screenName,
  }: {
    productId: TProductId
    screenName: ScreenName
  }): void {
    const event = Events.PURCHASE_SCREEN_CLOSED
    const eventProperty = {
      product_id: productId,
      screen_name: screenName,
    }
    this.logEventOrPushToQueue(event, eventProperty)
  }

  private logEventOrPushToQueue(
    event: Events,
    eventProperty?: Record<string, any>,
    cb?: () => void,
  ): void {
    if (this.loggers?.size) {
      this.logEvent(event, eventProperty, cb)
    } else {
      this.eventsQueue.push({ event, eventProperty })
    }
  }

  private notifyInitFinished() {
    if (this.eventsQueue.length) {
      this.eventsQueue.forEach(({ event, eventProperty }) =>
        this.logEvent(event, eventProperty),
      )
      this.eventsQueue = []
    }
  }

  private logEvent(
    event: Events,
    eventProperty?: Record<string, any>,
    cb?: () => void,
  ): void {
    this.loggers?.forEach((logger) => logger.log(event, eventProperty, cb))
  }
}

export const eventLogger = new EventLoggerService()
